import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Paper,
  useTheme,
  IconButton,
  InputAdornment,
  Avatar,
  Container,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save,
  Cancel,
  Person,
  FamilyRestroom,
  Psychology,
  Info,
  Phone,
  Email,
  Home,
  Work,
  PhotoCamera,
  CameraAlt,
  Collections,
  Visibility,
  VisibilityOff,
  Lock,
  ExitToApp
} from '@mui/icons-material';

// Configuração da API
const API_BASE_URL = (() => {
  const raw = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';
  return raw.replace(/\/+$/, '').replace(/\/api$/i, '');
})();

// =====================================================
// INTERFACES TYPESCRIPT
// =====================================================

interface LoginData {
  token: string;
  ultimosDigitos: string;
}

interface ClienteData {
  // Aba 1 - Dados Pessoais
  nome: string;
  foto: string;
  genero_id: string;
  estado_civil_id: string;
  escolaridade_id: string;
  abordagem_id: string;
  tipo_cliente_id: string;
  idade: string;
  ocupacao: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  celular: string;
  email: string;
  emergencia_ligar_para: string;
  emergencia_fone: string;
  reside_com: string;
  nome_conjuge: string;
  idade_conjuge: string;
  escolaridade_conjuge: string;
  ocupacao_conjuge: string;
  filhos: string;
  
  // Aba 2 - Filiação
  nome_pai: string;
  idade_pai: string;
  ocupacao_pai: string;
  escolaridade_pai: string;
  nome_mae: string;
  idade_mae: string;
  escolaridade_mae: string;
  ocupacao_mae: string;
  
  // Aba 3 - Anamnese
  queixa_principal: string;
  mudancas_periodo: string;
  sintomas_sinais: string;
  acontecimento_marcante: string;
  relacionamento_familiar: string;
  outras_obs: string;
  tentativa_suicidio_sn: boolean;
  tentativa_suicidio: string;
  tratamento_psicologico_sn: boolean;
  tratamento_psicologico: string;
  alguma_doenca_sn: boolean;
  alguma_doenca: string;
  uso_medicamentos_sn: boolean;
  uso_medicamento: string;
  familiar_doenca_mental_sn: boolean;
  familiar_doenca_mental: string;
  abuso_alcool_sn: boolean;
  abuso_alcool: string;
  obs_geral: string;
}

interface SelectOptions {
  generos: Array<{ id: number; nome: string }>;
  estadosCivis: Array<{ id: number; nome: string }>;
  escolaridades: Array<{ id: number; nome: string }>;
  abordagens: Array<{ id: number; nome: string }>;
  tiposCliente: Array<{ id: number; nome: string }>;
  ufs: string[];
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const MeuCadastro: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados principais
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [logoutDialog, setLogoutDialog] = useState<boolean>(false);
  const [successDialog, setSuccessDialog] = useState<boolean>(false);
  
  // useEffect para definir token da URL
  useEffect(() => {
    if (token) {
      setLoginData(prev => ({ ...prev, token }));
    }
  }, [token]);
  
  // Dados de login
  const [loginData, setLoginData] = useState<LoginData>({
    token: '',
    ultimosDigitos: ''
  });
  
  // Dados do cliente
  const [clienteData, setClienteData] = useState<ClienteData>({
    // Aba 1 - Dados Pessoais
    nome: '',
    foto: '',
    genero_id: '',
    estado_civil_id: '',
    escolaridade_id: '',
    abordagem_id: '',
    tipo_cliente_id: '',
    idade: '',
    ocupacao: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    celular: '',
    email: '',
    emergencia_ligar_para: '',
    emergencia_fone: '',
    reside_com: '',
    nome_conjuge: '',
    idade_conjuge: '',
    escolaridade_conjuge: '',
    ocupacao_conjuge: '',
    filhos: '',
    
    // Aba 2 - Filiação
    nome_pai: '',
    idade_pai: '',
    ocupacao_pai: '',
    escolaridade_pai: '',
    nome_mae: '',
    idade_mae: '',
    escolaridade_mae: '',
    ocupacao_mae: '',
    
    // Aba 3 - Anamnese
    queixa_principal: '',
    mudancas_periodo: '',
    sintomas_sinais: '',
    acontecimento_marcante: '',
    relacionamento_familiar: '',
    outras_obs: '',
    tentativa_suicidio_sn: false,
    tentativa_suicidio: '',
    tratamento_psicologico_sn: false,
    tratamento_psicologico: '',
    alguma_doenca_sn: false,
    alguma_doenca: '',
    uso_medicamentos_sn: false,
    uso_medicamento: '',
    familiar_doenca_mental_sn: false,
    familiar_doenca_mental: '',
    abuso_alcool_sn: false,
    abuso_alcool: '',
    obs_geral: ''
  });
  
  // Opções para os selects
  const [selectOptions] = useState<SelectOptions>({
    generos: [
      { id: 1, nome: 'Masculino' },
      { id: 2, nome: 'Feminino' },
      { id: 3, nome: 'Outro' }
    ],
    estadosCivis: [
      { id: 1, nome: 'Solteiro(a)' },
      { id: 2, nome: 'Casado(a)' },
      { id: 3, nome: 'Divorciado(a)' },
      { id: 4, nome: 'Viúvo(a)' },
      { id: 5, nome: 'União Estável' }
    ],
    escolaridades: [
      { id: 1, nome: 'Fundamental Incompleto' },
      { id: 2, nome: 'Fundamental Completo' },
      { id: 3, nome: 'Médio Incompleto' },
      { id: 4, nome: 'Médio Completo' },
      { id: 5, nome: 'Superior Incompleto' },
      { id: 6, nome: 'Superior Completo' },
      { id: 7, nome: 'Pós-graduação' }
    ],
    abordagens: [
      { id: 1, nome: 'Gestalt' },
      { id: 2, nome: 'TCC' },
      { id: 3, nome: 'Psicanálise' }
    ],
    tiposCliente: [
      { id: 1, nome: 'Individual' },
      { id: 2, nome: 'Casal' },
      { id: 3, nome: 'Família' },
      { id: 4, nome: 'Grupo' }
    ],
    ufs: [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
      'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
      'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ]
  });
  
  // =====================================================
  // FUNÇÕES DE AUTENTICAÇÃO
  // =====================================================
  
  const handleLogin = async () => {
    if (!loginData.token.trim() || !loginData.ultimosDigitos.trim()) {
      setError('Por favor, preencha todos os campos de acesso.');
      return;
    }
    
    if (loginData.ultimosDigitos.length !== 4) {
      setError('A senha deve conter exatamente 4 números.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_BASE_URL}/api/meu-cadastro/validar-acesso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: loginData.token.trim(),
          ultimosDigitos: loginData.ultimosDigitos.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
          setIsAuthenticated(true);
          await carregarDadosCliente();
        } else {
          setError(data.message || 'Token ou senha de acesso inválidos.');
        }
    } catch (error) {
      console.error('Erro ao validar acesso:', error);
      setError('Erro ao conectar com o servidor. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const carregarDadosCliente = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/meu-cadastro/dados/${loginData.token}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        const cliente = data.data;
        setClienteData({
          // Mapear todos os campos do cliente
          nome: cliente.nome || '',
          foto: cliente.foto || '',
          genero_id: cliente.genero?.toString() || '',
          estado_civil_id: cliente.estado_civil?.toString() || '',
          escolaridade_id: cliente.escolaridade?.toString() || '',
          abordagem_id: cliente.abordagem_id?.toString() || '',
          tipo_cliente_id: cliente.tipo_cliente?.toString() || '',
          idade: cliente.data_nascimento || '',
          ocupacao: cliente.ocupacao || cliente.profissao || '',
          cep: cliente.cep || '',
          endereco: cliente.endereco || '',
          numero: cliente.numero || '',
          complemento: cliente.complemento || '',
          bairro: cliente.bairro || '',
          cidade: cliente.cidade || '',
          uf: cliente.uf || '',
          celular: cliente.celular || '',
          email: cliente.email || '',
          emergencia_ligar_para: cliente.emergencia_ligar_para || '',
          emergencia_fone: cliente.emergencia_fone || '',
          reside_com: cliente.reside_com || '',
          nome_conjuge: cliente.nome_conjuge || '',
          idade_conjuge: cliente.idade_conjuge?.toString() || '',
          escolaridade_conjuge: cliente.escolaridade_conjuge || '',
          ocupacao_conjuge: cliente.ocupacao_conjuge || '',
          filhos: cliente.filhos || '',
          nome_pai: cliente.nome_pai || '',
          idade_pai: cliente.idade_pai?.toString() || '',
          ocupacao_pai: cliente.ocupacao_pai || '',
          escolaridade_pai: cliente.escolaridade_pai || '',
          nome_mae: cliente.nome_mae || '',
          idade_mae: cliente.idade_mae?.toString() || '',
          escolaridade_mae: cliente.escolaridade_mae || '',
          ocupacao_mae: cliente.ocupacao_mae || '',
          queixa_principal: cliente.queixa_principal || '',
          mudancas_periodo: cliente.mudancas_periodo || '',
          sintomas_sinais: cliente.sintomas_sinais || '',
          acontecimento_marcante: cliente.acontecimento_marcante || '',
          relacionamento_familiar: cliente.relacionamento_familiar || '',
          outras_obs: cliente.outras_obs || '',
          tentativa_suicidio_sn: Boolean(cliente.tentativa_suicidio_sn),
          tentativa_suicidio: cliente.tentativa_suicidio || '',
          tratamento_psicologico_sn: Boolean(cliente.tratamento_psicologico_sn),
          tratamento_psicologico: cliente.tratamento_psicologico || '',
          alguma_doenca_sn: Boolean(cliente.alguma_doenca_sn),
          alguma_doenca: cliente.alguma_doenca || '',
          uso_medicamentos_sn: Boolean(cliente.uso_medicamentos_sn),
          uso_medicamento: cliente.uso_medicamento || '',
          familiar_doenca_mental_sn: Boolean(cliente.familiar_doenca_mental_sn),
          familiar_doenca_mental: cliente.familiar_doenca_mental || '',
          abuso_alcool_sn: Boolean(cliente.abuso_alcool_sn),
          abuso_alcool: cliente.abuso_alcool || '',
          obs_geral: cliente.obs_geral || ''
        });
      } else {
        setError(data.message || 'Erro ao carregar dados do cliente.');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await fetch(`${API_BASE_URL}/api/meu-cadastro/atualizar/${loginData.token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clienteData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessDialog(true); // Mostrar dialog de sucesso
      } else {
        setError(data.message || 'Erro ao salvar dados.');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setError('Erro ao salvar dados. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleLogout = () => {
    const currentToken = loginData.token; // Salvar o token atual
    setIsAuthenticated(false);
    setLoginData({ 
      token: currentToken, // Manter o token
      ultimosDigitos: ''   // Limpar apenas a senha
    });
    setClienteData({
      // Reset todos os campos
      nome: '', foto: '', genero_id: '', estado_civil_id: '', escolaridade_id: '',
      abordagem_id: '', tipo_cliente_id: '', idade: '', ocupacao: '', cep: '',
      endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
      celular: '', email: '', emergencia_ligar_para: '', emergencia_fone: '',
      reside_com: '', nome_conjuge: '', idade_conjuge: '', escolaridade_conjuge: '',
      ocupacao_conjuge: '', filhos: '', nome_pai: '', idade_pai: '', ocupacao_pai: '',
      escolaridade_pai: '', nome_mae: '', idade_mae: '', escolaridade_mae: '',
      ocupacao_mae: '', queixa_principal: '', mudancas_periodo: '', sintomas_sinais: '',
      acontecimento_marcante: '', relacionamento_familiar: '', outras_obs: '',
      tentativa_suicidio_sn: false, tentativa_suicidio: '', tratamento_psicologico_sn: false,
      tratamento_psicologico: '', alguma_doenca_sn: false, alguma_doenca: '',
      uso_medicamentos_sn: false, uso_medicamento: '', familiar_doenca_mental_sn: false,
      familiar_doenca_mental: '', abuso_alcool_sn: false, abuso_alcool: '', obs_geral: ''
    });
    setActiveTab(0);
    setError('');
    setSuccess('');
    setLogoutDialog(false);
  };
  
  const handleSuccessConfirm = () => {
    setSuccessDialog(false);
    handleLogout();
  };
  
  // =====================================================
  // HANDLERS DE FORMULÁRIO
  // =====================================================
  
  const handleLoginChange = (field: keyof LoginData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError(''); // Limpar erro ao digitar
  };
  
  const handleClienteChange = (field: keyof ClienteData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }) => {
    const value = event.target.value;
    setClienteData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleSwitchChange = (field: keyof ClienteData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setClienteData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };
  
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Verificar se é uma imagem
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem.');
        return;
      }
      
      // Verificar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setClienteData(prev => ({
          ...prev,
          foto: base64String
        }));
        setError(''); // Limpar erro
      };
      reader.readAsDataURL(file);
    }
  };
  
  // =====================================================
  // RENDERIZAÇÃO CONDICIONAL - TELA DE LOGIN
  // =====================================================
  
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box textAlign="center" mb={4}>
            <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
              <Lock />
            </Avatar>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Meu Cadastro
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Acesse seus dados pessoais
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <TextField
              fullWidth
              label="Token de Acesso"
              value={loginData.token}
              onChange={handleLoginChange('token')}
              variant="outlined"
              disabled={true}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Senha de Acesso"
              type={showPassword ? 'text' : 'password'}
              value={loginData.ultimosDigitos}
              onChange={handleLoginChange('ultimosDigitos')}
              variant="outlined"
              inputProps={{ maxLength: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Acessar'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }
  
  // =====================================================
  // RENDERIZAÇÃO - FORMULÁRIO PRINCIPAL
  // =====================================================
  
  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" color="primary">
              Meu Cadastro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clienteData.nome || 'Carregando...'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ExitToApp />}
            onClick={() => setLogoutDialog(true)}
            size={isMobile ? "small" : "medium"}
          >
            Sair
          </Button>
        </Box>
      </Paper>
      
      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Formulário */}
      <Card elevation={3}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab icon={<Person />} label="Dados Pessoais" />
            <Tab icon={<FamilyRestroom />} label="Filiação" />
            <Tab icon={<Psychology />} label="Anamnese" />
          </Tabs>
          
          {/* Aba 1 - Dados Pessoais */}
          {activeTab === 0 && (
            <Box>
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12}>
                  <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                    <Avatar
                      src={clienteData.foto}
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      <Person sx={{ fontSize: 60 }} />
                    </Avatar>
                    <Box display="flex" gap={1}>
                      {/* Botão Câmera - Ativar câmera real */}
                      <input
                        accept="image/*;capture=camera"
                        style={{ display: 'none' }}
                        id="camera-upload"
                        type="file"
                        multiple={false}
                        capture="environment"  // Câmera traseira, ou "user" para frontal
                        onChange={handlePhotoUpload}
                      />
                      <label htmlFor="camera-upload">
                        <Button
                          variant="outlined"
                          startIcon={<CameraAlt />}
                          size="small"
                          component="span"
                          sx={{ textTransform: 'none' }}
                        >
                          Câmera
                        </Button>
                      </label>
                      
                      {/* Botão Galeria - Remover capture para acessar galeria */}
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="galeria-upload"
                        type="file"
                        multiple={false}
                        // Remover o capture aqui para acessar galeria
                        onChange={handlePhotoUpload}
                      />
                      <label htmlFor="galeria-upload">
                        <Button
                          variant="outlined"
                          startIcon={<Collections />}
                          size="small"
                          component="span"
                          sx={{ textTransform: 'none' }}
                        >
                          Galeria
                        </Button>
                      </label>
                      
                      {/* Botão Remover */}
                      {clienteData.foto && (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => setClienteData(prev => ({ ...prev, foto: '' }))}
                          sx={{ textTransform: 'none' }}
                        >
                          Remover
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome Completo"
                    value={clienteData.nome}
                    onChange={handleClienteChange('nome')}
                    required
                    InputProps={{
                      style: { fontWeight: 'bold' }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gênero</InputLabel>
                    <Select
                      value={clienteData.genero_id}
                      onChange={handleClienteChange('genero_id')}
                      label="Gênero"
                    >
                      {selectOptions.generos.map(genero => (
                        <MenuItem key={genero.id} value={genero.id.toString()}>
                          {genero.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data de Nascimento"
                    value={clienteData.idade}
                    onChange={handleClienteChange('idade')}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Estado Civil</InputLabel>
                    <Select
                      value={clienteData.estado_civil_id}
                      onChange={handleClienteChange('estado_civil_id')}
                      label="Estado Civil"
                    >
                      {selectOptions.estadosCivis.map(estado => (
                        <MenuItem key={estado.id} value={estado.id.toString()}>
                          {estado.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Escolaridade</InputLabel>
                    <Select
                      value={clienteData.escolaridade_id}
                      onChange={handleClienteChange('escolaridade_id')}
                      label="Escolaridade"
                    >
                      {selectOptions.escolaridades.map(escolaridade => (
                        <MenuItem key={escolaridade.id} value={escolaridade.id.toString()}>
                          {escolaridade.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ocupação"
                    value={clienteData.ocupacao}
                    onChange={handleClienteChange('ocupacao')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom color="primary">
                    Endereço
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="CEP"
                    value={clienteData.cep}
                    onChange={handleClienteChange('cep')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    value={clienteData.endereco}
                    onChange={handleClienteChange('endereco')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={clienteData.numero}
                    onChange={handleClienteChange('numero')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Complemento"
                    value={clienteData.complemento}
                    onChange={handleClienteChange('complemento')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    value={clienteData.bairro}
                    onChange={handleClienteChange('bairro')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={clienteData.cidade}
                    onChange={handleClienteChange('cidade')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>UF</InputLabel>
                    <Select
                      value={clienteData.uf}
                      onChange={handleClienteChange('uf')}
                      label="UF"
                    >
                      {selectOptions.ufs.map(uf => (
                        <MenuItem key={uf} value={uf}>
                          {uf}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom color="primary">
                    Contato
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Celular"
                    value={clienteData.celular}
                    onChange={handleClienteChange('celular')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    value={clienteData.email}
                    onChange={handleClienteChange('email')}
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Em caso de emergência, ligar para"
                    value={clienteData.emergencia_ligar_para}
                    onChange={handleClienteChange('emergencia_ligar_para')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefone de emergência"
                    value={clienteData.emergencia_fone}
                    onChange={handleClienteChange('emergencia_fone')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom color="primary">
                    Informações Familiares
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reside com"
                    value={clienteData.reside_com}
                    onChange={handleClienteChange('reside_com')}
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome do cônjuge"
                    value={clienteData.nome_conjuge}
                    onChange={handleClienteChange('nome_conjuge')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Idade do cônjuge"
                    value={clienteData.idade_conjuge}
                    onChange={handleClienteChange('idade_conjuge')}
                    type="number"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Escolaridade do cônjuge"
                    value={clienteData.escolaridade_conjuge}
                    onChange={handleClienteChange('escolaridade_conjuge')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ocupação do cônjuge"
                    value={clienteData.ocupacao_conjuge}
                    onChange={handleClienteChange('ocupacao_conjuge')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Filhos"
                    value={clienteData.filhos}
                    onChange={handleClienteChange('filhos')}
                    multiline
                    rows={3}
                    placeholder="Descreva informações sobre os filhos (nomes, idades, etc.)"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Aba 2 - Filiação */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Informações dos Pais
              </Typography>
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                    Pai
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome do pai"
                    value={clienteData.nome_pai}
                    onChange={handleClienteChange('nome_pai')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Idade do pai"
                    value={clienteData.idade_pai}
                    onChange={handleClienteChange('idade_pai')}
                    type="number"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ocupação do pai"
                    value={clienteData.ocupacao_pai}
                    onChange={handleClienteChange('ocupacao_pai')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Escolaridade do pai"
                    value={clienteData.escolaridade_pai}
                    onChange={handleClienteChange('escolaridade_pai')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Mãe
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome da mãe"
                    value={clienteData.nome_mae}
                    onChange={handleClienteChange('nome_mae')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Idade da mãe"
                    value={clienteData.idade_mae}
                    onChange={handleClienteChange('idade_mae')}
                    type="number"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ocupação da mãe"
                    value={clienteData.ocupacao_mae}
                    onChange={handleClienteChange('ocupacao_mae')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Escolaridade da mãe"
                    value={clienteData.escolaridade_mae}
                    onChange={handleClienteChange('escolaridade_mae')}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Aba 3 - Anamnese */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Anamnese Psicológica
              </Typography>
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Queixa principal"
                    value={clienteData.queixa_principal}
                    onChange={handleClienteChange('queixa_principal')}
                    multiline
                    rows={4}
                    placeholder="Descreva o motivo principal da busca por atendimento psicológico"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mudanças no período"
                    value={clienteData.mudancas_periodo}
                    onChange={handleClienteChange('mudancas_periodo')}
                    multiline
                    rows={3}
                    placeholder="Descreva mudanças significativas que ocorreram recentemente"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sintomas e sinais"
                    value={clienteData.sintomas_sinais}
                    onChange={handleClienteChange('sintomas_sinais')}
                    multiline
                    rows={3}
                    placeholder="Descreva sintomas físicos ou emocionais que tem apresentado"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Acontecimento marcante"
                    value={clienteData.acontecimento_marcante}
                    onChange={handleClienteChange('acontecimento_marcante')}
                    multiline
                    rows={3}
                    placeholder="Descreva algum acontecimento marcante em sua vida"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Relacionamento familiar"
                    value={clienteData.relacionamento_familiar}
                    onChange={handleClienteChange('relacionamento_familiar')}
                    multiline
                    rows={3}
                    placeholder="Como é o relacionamento com sua família?"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Histórico de Saúde Mental
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.tentativa_suicidio_sn}
                        onChange={handleSwitchChange('tentativa_suicidio_sn')}
                      />
                    }
                    label="Já teve tentativa de suicídio?"
                  />
                  {clienteData.tentativa_suicidio_sn && (
                    <TextField
                      fullWidth
                      label="Descreva a tentativa de suicídio"
                      value={clienteData.tentativa_suicidio}
                      onChange={handleClienteChange('tentativa_suicidio')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.tratamento_psicologico_sn}
                        onChange={handleSwitchChange('tratamento_psicologico_sn')}
                      />
                    }
                    label="Já fez tratamento psicológico?"
                  />
                  {clienteData.tratamento_psicologico_sn && (
                    <TextField
                      fullWidth
                      label="Descreva o tratamento psicológico"
                      value={clienteData.tratamento_psicologico}
                      onChange={handleClienteChange('tratamento_psicologico')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Histórico de Saúde Física
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.alguma_doenca_sn}
                        onChange={handleSwitchChange('alguma_doenca_sn')}
                      />
                    }
                    label="Possui alguma doença?"
                  />
                  {clienteData.alguma_doenca_sn && (
                    <TextField
                      fullWidth
                      label="Descreva a doença"
                      value={clienteData.alguma_doenca}
                      onChange={handleClienteChange('alguma_doenca')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.uso_medicamentos_sn}
                        onChange={handleSwitchChange('uso_medicamentos_sn')}
                      />
                    }
                    label="Faz uso de medicamentos?"
                  />
                  {clienteData.uso_medicamentos_sn && (
                    <TextField
                      fullWidth
                      label="Descreva os medicamentos"
                      value={clienteData.uso_medicamento}
                      onChange={handleClienteChange('uso_medicamento')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Histórico Familiar
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.familiar_doenca_mental_sn}
                        onChange={handleSwitchChange('familiar_doenca_mental_sn')}
                      />
                    }
                    label="Algum familiar tem doença mental?"
                  />
                  {clienteData.familiar_doenca_mental_sn && (
                    <TextField
                      fullWidth
                      label="Descreva a doença mental familiar"
                      value={clienteData.familiar_doenca_mental}
                      onChange={handleClienteChange('familiar_doenca_mental')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clienteData.abuso_alcool_sn}
                        onChange={handleSwitchChange('abuso_alcool_sn')}
                      />
                    }
                    label="Há abuso de álcool na família?"
                  />
                  {clienteData.abuso_alcool_sn && (
                    <TextField
                      fullWidth
                      label="Descreva o abuso de álcool"
                      value={clienteData.abuso_alcool}
                      onChange={handleClienteChange('abuso_alcool')}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações gerais"
                    value={clienteData.obs_geral}
                    onChange={handleClienteChange('obs_geral')}
                    multiline
                    rows={4}
                    placeholder="Outras informações que considera importantes"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Botões de ação */}
          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 150 }}
            >
              {saving ? <CircularProgress size={20} /> : 'Salvar Dados'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Dialog de sucesso */}
      <Dialog 
        open={successDialog} 
        onClose={() => {}} // Não permitir fechar clicando fora
        disableEscapeKeyDown // Não permitir fechar com ESC
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
          ✅ Sucesso!
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', mb: 2 }}>
            Seus dados foram atualizados com sucesso!
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            Você será redirecionado para a tela de login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleSuccessConfirm} 
            variant="contained" 
            color="primary"
            size="large"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog de confirmação de logout */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)}>
        <DialogTitle>Confirmar Saída</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja sair? Todas as alterações não salvas serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)}>Cancelar</Button>
          <Button onClick={handleLogout} color="error" variant="contained">
            Sair
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MeuCadastro;

